/**
 * FHIR Normalizers (Healthcare)
 *
 * DRR-027: Normalizers for FHIR resources from the medplum subgraph
 *
 * STUB: Expand when FHIR types are available in generated/graphql.ts
 *
 * These normalizers will convert FHIR resources (Patient, Encounter, Claim)
 * into NormalizedEntity<T> with HIPAA-compliant safety levels.
 */

import { SafetyLevel } from '@/types/graphql-compat';

import type {
  NormalizedEntity,
  ComplianceFramework,
} from '@/types/normalized-entity';

import { UnifiedStatus } from '@/types/normalized-entity';

// ============================================================================
// PLACEHOLDER TYPES
// These will be replaced by imports from @/generated/graphql when available
// ============================================================================

/**
 * Placeholder Patient type - will be replaced by FHIR Patient from medplum
 */
export interface FhirPatient {
  __typename: 'Patient';
  id: string;
  resourceType: 'Patient';
  active?: boolean;
  name?: Array<{
    family?: string;
    given?: string[];
  }>;
  birthDate?: string;
  gender?: 'male' | 'female' | 'other' | 'unknown';
  identifier?: Array<{
    system?: string;
    value?: string;
  }>;
}

/**
 * Placeholder Encounter type - will be replaced by FHIR Encounter from medplum
 */
export interface FhirEncounter {
  __typename: 'Encounter';
  id: string;
  resourceType: 'Encounter';
  status?: 'planned' | 'arrived' | 'triaged' | 'in-progress' | 'onleave' | 'finished' | 'cancelled';
  class?: {
    code?: string;
    display?: string;
  };
  subject?: {
    reference?: string;
  };
  period?: {
    start?: string;
    end?: string;
  };
  type?: Array<{
    coding?: Array<{
      system?: string;
      code?: string;
      display?: string;
    }>;
  }>;
}

/**
 * Placeholder Claim type - will be replaced by FHIR Claim from medplum
 */
export interface FhirClaim {
  __typename: 'Claim';
  id: string;
  resourceType: 'Claim';
  status?: 'active' | 'cancelled' | 'draft' | 'entered-in-error';
  type?: {
    coding?: Array<{
      system?: string;
      code?: string;
      display?: string;
    }>;
  };
  use?: 'claim' | 'preauthorization' | 'predetermination';
  patient?: {
    reference?: string;
  };
  created?: string;
  provider?: {
    reference?: string;
  };
  priority?: {
    coding?: Array<{
      code?: string;
    }>;
  };
  total?: {
    value?: number;
    currency?: string;
  };
}

// ============================================================================
// STATUS MAPPINGS
// ============================================================================

/**
 * Map FHIR Encounter status to UnifiedStatus
 */
const ENCOUNTER_STATUS_MAP: Record<NonNullable<FhirEncounter['status']>, UnifiedStatus> = {
  planned: UnifiedStatus.PENDING,
  arrived: UnifiedStatus.PENDING,
  triaged: UnifiedStatus.PENDING,
  'in-progress': UnifiedStatus.ACTIVE,
  onleave: UnifiedStatus.BLOCKED,
  finished: UnifiedStatus.COMPLETED,
  cancelled: UnifiedStatus.CANCELLED,
} as const;

/**
 * Map FHIR Claim status to UnifiedStatus
 */
const CLAIM_STATUS_MAP: Record<NonNullable<FhirClaim['status']>, UnifiedStatus> = {
  draft: UnifiedStatus.PENDING,
  active: UnifiedStatus.ACTIVE,
  cancelled: UnifiedStatus.CANCELLED,
  'entered-in-error': UnifiedStatus.FAILED,
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Extract display name from FHIR Patient name
 */
function getPatientDisplayName(patient: FhirPatient): string {
  const name = patient.name?.[0];
  if (!name) {
    return `Patient ${patient.id}`;
  }

  const given = name.given?.join(' ') ?? '';
  const family = name.family ?? '';

  if (given && family) {
    return `${given} ${family}`;
  }
  return given || family || `Patient ${patient.id}`;
}

/**
 * Extract display name from FHIR Encounter type
 */
function getEncounterDisplayName(encounter: FhirEncounter): string {
  const typeDisplay = encounter.type?.[0]?.coding?.[0]?.display;
  const classDisplay = encounter.class?.display;

  if (typeDisplay) {
    return typeDisplay;
  }
  if (classDisplay) {
    return `${classDisplay} Encounter`;
  }
  return `Encounter ${encounter.id}`;
}

/**
 * Extract display name from FHIR Claim
 */
function getClaimDisplayName(claim: FhirClaim): string {
  const typeDisplay = claim.type?.coding?.[0]?.display;
  const use = claim.use;

  if (typeDisplay && use) {
    return `${typeDisplay} (${use})`;
  }
  if (typeDisplay) {
    return typeDisplay;
  }
  return `Claim ${claim.id}`;
}

/**
 * Determine if claim requires dual approval based on amount
 */
function requiresDualApprovalForClaim(claim: FhirClaim): boolean {
  const amount = claim.total?.value ?? 0;
  // Claims over $10,000 require dual approval for financial controls
  return amount > 10000;
}

// ============================================================================
// PATIENT NORMALIZER
// ============================================================================

/**
 * Normalize a FHIR Patient into NormalizedEntity<FhirPatient>
 *
 * Healthcare patient data is ALWAYS LIFE_CRITICAL and HIPAA compliant.
 *
 * @param patient - The Patient from FHIR
 * @returns Fully typed NormalizedEntity with Patient preserved in domainData
 */
export function normalizePatient(patient: FhirPatient): NormalizedEntity<FhirPatient> {
  // Patient data is always life-critical (PHI)
  const safetyLevel = SafetyLevel.LIFE_CRITICAL;

  // Map active status to unified status
  const unifiedStatus = patient.active === false
    ? UnifiedStatus.BLOCKED
    : UnifiedStatus.ACTIVE;

  return {
    id: patient.id,
    displayName: getPatientDisplayName(patient),
    sourceSubgraph: 'fhir',
    sourceType: 'Patient',
    safetyLevel,
    requiresDualApproval: true, // PHI always requires dual approval
    complianceFrameworks: ['HIPAA'] as ComplianceFramework[],
    unifiedStatus,
    isBlockedBySafety: patient.active === false,
    domainData: patient,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// ENCOUNTER NORMALIZER
// ============================================================================

/**
 * Normalize a FHIR Encounter into NormalizedEntity<FhirEncounter>
 *
 * Clinical encounters are CRITICAL and HIPAA compliant.
 *
 * @param encounter - The Encounter from FHIR
 * @returns Fully typed NormalizedEntity with Encounter preserved in domainData
 */
export function normalizeEncounter(encounter: FhirEncounter): NormalizedEntity<FhirEncounter> {
  // Clinical encounters are critical (affect patient care)
  const safetyLevel = SafetyLevel.CRITICAL;

  // Map status to unified status
  const unifiedStatus = encounter.status
    ? (ENCOUNTER_STATUS_MAP[encounter.status] ?? UnifiedStatus.PENDING)
    : UnifiedStatus.PENDING;

  // Encounters are blocked if on leave or cancelled
  const isBlockedBySafety = encounter.status === 'onleave' || encounter.status === 'cancelled';

  return {
    id: encounter.id,
    displayName: getEncounterDisplayName(encounter),
    sourceSubgraph: 'fhir',
    sourceType: 'Encounter',
    safetyLevel,
    requiresDualApproval: true, // Clinical data requires dual approval
    complianceFrameworks: ['HIPAA'] as ComplianceFramework[],
    unifiedStatus,
    isBlockedBySafety,
    domainData: encounter,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// CLAIM NORMALIZER
// ============================================================================

/**
 * Normalize a FHIR Claim into NormalizedEntity<FhirClaim>
 *
 * Claims are HIGH priority with both HIPAA and SOX compliance.
 *
 * @param claim - The Claim from FHIR
 * @returns Fully typed NormalizedEntity with Claim preserved in domainData
 */
export function normalizeClaim(claim: FhirClaim): NormalizedEntity<FhirClaim> {
  // Claims are HIGH priority (financial + PHI)
  const safetyLevel = SafetyLevel.HIGH;

  // Map status to unified status
  const unifiedStatus = claim.status
    ? (CLAIM_STATUS_MAP[claim.status] ?? UnifiedStatus.PENDING)
    : UnifiedStatus.PENDING;

  // Claims are blocked if cancelled or entered-in-error
  const isBlockedBySafety = claim.status === 'cancelled' || claim.status === 'entered-in-error';

  return {
    id: claim.id,
    displayName: getClaimDisplayName(claim),
    sourceSubgraph: 'fhir',
    sourceType: 'Claim',
    safetyLevel,
    requiresDualApproval: requiresDualApprovalForClaim(claim),
    complianceFrameworks: ['HIPAA', 'SOX'] as ComplianceFramework[],
    unifiedStatus,
    isBlockedBySafety,
    domainData: claim,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Check if an object is a FHIR Patient
 */
export function isPatient(obj: unknown): obj is FhirPatient {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Patient'
  );
}

/**
 * Check if an object is a FHIR Encounter
 */
export function isEncounter(obj: unknown): obj is FhirEncounter {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Encounter'
  );
}

/**
 * Check if an object is a FHIR Claim
 */
export function isClaim(obj: unknown): obj is FhirClaim {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Claim'
  );
}
